/**
 * VB Server - Speakers API
 *
 * CRUD operations for organization speakers (known voice identities).
 *
 * @example
 * import { listSpeakers, createSpeaker, updateSpeaker, deleteSpeaker } from '@/api/server/speakers';
 *
 * const speakers = await listSpeakers(orgId);
 * const newSpeaker = await createSpeaker(orgId, { name: 'John Doe' });
 */

import { serverGet, serverPost, serverPut, serverDelete } from './client';

// =============================================================================
// Types
// =============================================================================

/**
 * Speaker entity from vb-server
 */
export interface Speaker {
  speaker_id: string;
  org_id: string;
  name: string;
  image?: string | null;
  description?: string | null;
  status: number;
  created: string;
  modified: string;
}

/**
 * Input for creating a new speaker
 */
export interface CreateSpeakerInput {
  name: string;
  image?: string;
  description?: string;
}

/**
 * Input for updating a speaker
 */
export interface UpdateSpeakerInput {
  name?: string;
  image?: string;
  description?: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * List all speakers for an organization
 *
 * @param orgId - Organization ID
 * @returns Array of speakers
 */
export async function listSpeakers(orgId: string): Promise<Speaker[]> {
  return serverGet<Speaker[]>(`/orgs/${orgId}/speakers`);
}

/**
 * Get a single speaker by ID
 *
 * @param orgId - Organization ID
 * @param speakerId - Speaker ID
 * @returns Speaker or null if not found
 */
export async function getSpeaker(orgId: string, speakerId: string): Promise<Speaker | null> {
  try {
    const speakers = await listSpeakers(orgId);
    return speakers.find((s) => s.speaker_id === speakerId) || null;
  } catch {
    return null;
  }
}

/**
 * Create a new speaker
 *
 * @param orgId - Organization ID
 * @param data - Speaker data
 * @returns Created speaker
 */
export async function createSpeaker(orgId: string, data: CreateSpeakerInput): Promise<Speaker> {
  return serverPost<Speaker>(`/orgs/${orgId}/speakers`, data);
}

/**
 * Update an existing speaker
 *
 * @param orgId - Organization ID
 * @param speakerId - Speaker ID
 * @param data - Updated speaker data
 * @returns Updated speaker
 */
export async function updateSpeaker(
  orgId: string,
  speakerId: string,
  data: UpdateSpeakerInput
): Promise<Speaker> {
  return serverPut<Speaker>(`/orgs/${orgId}/speakers/${speakerId}`, data);
}

/**
 * Delete a speaker (soft delete)
 *
 * @param orgId - Organization ID
 * @param speakerId - Speaker ID
 */
export async function deleteSpeaker(orgId: string, speakerId: string): Promise<void> {
  await serverDelete(`/orgs/${orgId}/speakers/${speakerId}`);
}

// =============================================================================
// Speaker-Radio ID Mappings
// =============================================================================

/**
 * Speaker-to-Radio ID mapping entity from vb-server
 */
export interface SpeakerRadioId {
  speaker_id: string;
  radio_id: string;
  org_id: string;
  speaker_name?: string;
  created?: string;
  created_by?: string;
}

/**
 * List all radio_id -> speaker mappings for an organization
 *
 * @param orgId - Organization ID
 * @returns Array of speaker-radio ID mappings
 */
export async function listSpeakerRadioIds(orgId: string): Promise<SpeakerRadioId[]> {
  return serverGet<SpeakerRadioId[]>(`/orgs/${orgId}/speaker-radio-ids`);
}

/**
 * Get radio_ids for a specific speaker
 *
 * @param orgId - Organization ID
 * @param speakerId - Speaker ID
 * @returns Array of speaker-radio ID mappings for this speaker
 */
export async function getSpeakerRadioIds(
  orgId: string,
  speakerId: string
): Promise<SpeakerRadioId[]> {
  return serverGet<SpeakerRadioId[]>(`/orgs/${orgId}/speakers/${speakerId}/radio-ids`);
}

/**
 * Set radio_ids for a speaker (replaces existing)
 *
 * @param orgId - Organization ID
 * @param speakerId - Speaker ID
 * @param radioIds - Array of radio IDs to assign
 */
export async function setSpeakerRadioIds(
  orgId: string,
  speakerId: string,
  radioIds: string[]
): Promise<void> {
  await serverPut(`/orgs/${orgId}/speakers/${speakerId}/radio-ids`, { radio_ids: radioIds });
}

/**
 * Remove a single radio_id mapping
 *
 * @param orgId - Organization ID
 * @param speakerId - Speaker ID
 * @param radioId - Radio ID to remove
 */
export async function removeSpeakerRadioId(
  orgId: string,
  speakerId: string,
  radioId: string
): Promise<void> {
  await serverDelete(`/orgs/${orgId}/speakers/${speakerId}/radio-ids/${radioId}`);
}
